"use client";

import React, { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { SectionReveal } from "./SectionReveal";
import Link from "next/link";
import useEmblaCarousel from "embla-carousel-react";
import { Car, Bike, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { vehicleImageUrl } from "@/lib/vehicle-api";

type VehicleItem = {
  id: number;
  title: string;
  slug: string;
  category: string;
  image: string;
  vehicleType: "car" | "bike";
};

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

import { getApiUrl } from "@/lib/api-config";

async function fetchFeaturedVehicles(
  vehicleType: "car" | "bike"
): Promise<VehicleItem[]> {
  const res = await fetch(
    getApiUrl(`/api/v1/web/featured-vehicles?vehicle_type=${vehicleType}`),
    { headers: { Accept: "application/json" } }
  );
  const json = await res.json();
  if (!json.success || !Array.isArray(json.data)) return [];
  return json.data.map((v: { id: number; name: string; slug?: string; body_type: string; image_url: string; vehicle_type: string }) => ({
    id: v.id,
    title: v.name,
    slug: v.slug || slugify(v.name),
    category: v.body_type,
    image: vehicleImageUrl(v.image_url),
    vehicleType: v.vehicle_type === "bike" ? "bike" : "car",
  }));
}

function VehicleCard({
  vehicle,
  index,
}: {
  vehicle: VehicleItem;
  index: number;
}) {
  const [imgError, setImgError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    setImgError(false);
    setRetryCount(0);
  }, [vehicle.image]);

  const handleImgError = () => {
    if (retryCount < 2) {
      setTimeout(() => {
        setRetryCount((prev) => prev + 1);
      }, 300);
    } else {
      setImgError(true);
    }
  };

  const detailsHref = `/fleet/${vehicle.slug}`;
  const imgSrc = retryCount > 0 ? `${vehicle.image}?retry=${retryCount}` : vehicle.image;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1.2, ease: [0.22, 0.61, 0.36, 1], delay: index * 0.05 }}
      className="flex-[0_0_90%] min-w-0 sm:flex-[0_0_45%] md:flex-[0_0_32%]"
    >
      <div className="group rounded-2xl overflow-hidden bg-white dark:bg-neutral-900/50 border border-neutral-200/60 dark:border-neutral-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
        <Link href={detailsHref} className="block relative h-48 overflow-hidden bg-gradient-to-br from-neutral-200 to-neutral-300 dark:from-neutral-700 dark:to-neutral-800">
          {!imgError ? (
            <img
              src={imgSrc}
              alt={vehicle.title}
              className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={handleImgError}
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center">
              {vehicle.vehicleType === "bike" ? (
                <Bike className="w-16 h-16 text-neutral-400 dark:text-neutral-500" aria-hidden />
              ) : (
                <Car className="w-16 h-16 text-neutral-400 dark:text-neutral-500" aria-hidden />
              )}
            </div>
          )}
        </Link>
        <div className="p-5">
          <div className="flex items-center justify-between gap-3 mb-1">
            <h3 className="text-lg font-bold text-[#131222] dark:text-white group-hover:text-[#FFB51D] transition-colors min-w-0">
              <Link href={detailsHref}>{vehicle.title}</Link>
            </h3>
            <Button asChild variant="outline" size="sm" className="rounded-lg border-[#FFB51D] text-[#FFB51D] hover:bg-[#FFB51D] hover:text-[#131222] dark:border-[#FFB51D] dark:text-[#FFB51D] dark:hover:bg-[#FFB51D] dark:hover:text-[#131222] shrink-0">
              <Link href={detailsHref}>View Details</Link>
            </Button>
          </div>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            {vehicle.category}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export function IKPopularCars() {
  const [activeTab, setActiveTab] = useState<"cars" | "bikes">("cars");
  const [vehicles, setVehicles] = useState<{ cars: VehicleItem[]; bikes: VehicleItem[] }>({
    cars: [],
    bikes: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchFeaturedVehicles("car"),
      fetchFeaturedVehicles("bike"),
    ])
      .then(([cars, bikes]) => {
        if (!cancelled) {
          setVehicles({ cars, bikes });
        }
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load vehicles");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const displayVehicles = activeTab === "cars" ? vehicles.cars : vehicles.bikes;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: "auto",
  });

  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, [emblaApi]);

  const onInit = useCallback(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    onInit();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onInit);
  }, [emblaApi, onInit, onSelect]);

  useEffect(() => {
    emblaApi?.reInit();
  }, [activeTab, emblaApi]);

  if (loading) {
    return (
      <div className="relative py-8 sm:py-10 md:py-12 lg:py-16 container mx-auto px-4 md:px-6">
        <div className="mb-12 text-center">
          <div className="h-10 bg-neutral-200 dark:bg-neutral-800 rounded w-64 mx-auto mb-6 animate-pulse" />
          <div className="h-10 bg-neutral-200 dark:bg-neutral-800 rounded-xl w-44 mx-auto animate-pulse" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, idx) => (
            <div
              key={idx}
              className="rounded-2xl border border-neutral-200/60 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 overflow-hidden shadow-lg animate-pulse"
            >
              <div className="h-48 bg-neutral-200 dark:bg-neutral-800" />
              <div className="p-5 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="h-6 bg-neutral-200 dark:bg-neutral-800 rounded w-1/2" />
                  <div className="h-8 bg-neutral-200 dark:bg-neutral-800 rounded-lg w-24 shrink-0" />
                </div>
                <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <SectionReveal
      className="relative py-8 sm:py-10 md:py-12 lg:py-16"
      aria-labelledby="popular-cars-heading"
    >
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1.2, ease: [0.22, 0.61, 0.36, 1], delay: 0.1 }}
          className="mb-12 text-center"
        >
          <h2
            id="popular-cars-heading"
            className="text-3xl md:text-4xl font-bold text-[#131222] dark:text-white mb-6"
          >
            Featured Cars & Bike
          </h2>
          <div
            role="tablist"
            aria-label="Select Cars or Bikes"
            className="inline-flex rounded-xl border-2 border-neutral-200 dark:border-neutral-700 p-0.5 bg-neutral-50 dark:bg-neutral-800/20"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "cars"}
              onClick={() => setActiveTab("cars")}
              className={`px-5 py-2 text-sm font-bold rounded-lg transition-colors flex items-center gap-2 ${
                activeTab === "cars"
                  ? "bg-[#FFB51D] text-[#131222] shadow-sm"
                  : "text-neutral-600 dark:text-neutral-400 hover:text-[#131222] dark:hover:text-white"
              }`}
            >
              <Car className="w-4 h-4" />
              Cars
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "bikes"}
              onClick={() => setActiveTab("bikes")}
              className={`px-5 py-2 text-sm font-bold rounded-lg transition-colors flex items-center gap-2 ${
                activeTab === "bikes"
                  ? "bg-[#FFB51D] text-[#131222] shadow-sm"
                  : "text-neutral-600 dark:text-neutral-400 hover:text-[#131222] dark:hover:text-white"
              }`}
            >
              <Bike className="w-4 h-4" />
              Bikes
            </button>
          </div>
        </motion.div>

        <div className="relative">
          {error && (
            <p className="text-center text-red-600 dark:text-red-400 py-8">
              {error}
            </p>
          )}
          {!loading && !error && (
            <>
              <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex gap-6 touch-pan-y">
                  {displayVehicles.map((vehicle, index) => (
                    <VehicleCard key={vehicle.id} vehicle={vehicle} index={index} />
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between mt-8 gap-4">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={scrollPrev}
                disabled={prevBtnDisabled}
                className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#FFB51D] text-[#FFB51D] bg-white shadow-md hover:bg-[#FFB51D] hover:text-[#131222] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#FFB51D] transition-colors"
                aria-label="Previous cars"
              >
                <ChevronLeft className="w-6 h-6" aria-hidden />
              </button>
              <button
                type="button"
                onClick={scrollNext}
                disabled={nextBtnDisabled}
                className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#FFB51D] text-[#FFB51D] bg-white shadow-md hover:bg-[#FFB51D] hover:text-[#131222] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#FFB51D] transition-colors"
                aria-label="Next cars"
              >
                <ChevronRight className="w-6 h-6" aria-hidden />
              </button>
            </div>

                {scrollSnaps.length > 1 && (
                  <div className="flex gap-1" role="tablist" aria-label="Carousel dots">
                    {scrollSnaps.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => scrollTo(index)}
                        role="tab"
                        aria-selected={index === selectedIndex}
                        aria-label={`Go to slide ${index + 1}`}
                        className="group min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full transition-all"
                      >
                        <span
                          className={`block rounded-full transition-all ${
                            index === selectedIndex
                              ? "w-8 h-2 bg-[#FFB51D]"
                              : "w-2 h-2 bg-[#131222]/50 group-hover:bg-[#131222]/70"
                          }`}
                          aria-hidden
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </SectionReveal>
  );
}
